import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PerformanceChart, { ChartDataPoint } from '../../components/PerformanceChart';
import { studentApi } from '../../services/api';
import StudentDailyData from './DailyData';
import StudentReports from './Reports';

type PageTab = 'performans' | 'gunluk' | 'raporlar';

// Ödev Özeti status colors. Green is a tint darker than the old #59e56e so the
// completion bar reads a touch more solid while staying close to the original.
const HW_GREEN = '#43c95c';
const HW_YELLOW = '#f5c518';
const HW_RED = '#e53e3e';

// Where a source-backed konu is studied in the library — resolved by the backend.
// owned → deep-linkable book+section; else a locked in-grade upsell.
interface LibraryRef {
  book_id: string;
  book_title: string;
  section_label: string | null;
  owned: boolean;
  locked_count?: number;
}

interface WeakTopic {
  topic: string;
  subject: string;
  wrongPct: number;
  libraryRef?: LibraryRef | null;
}

// These are PLE subject_unit konu-level stats (legacy field names stay for API compatibility).
// Field shape kept for backward compatibility with the existing card component.
interface TestResult {
  topic: string;
  detail: string;
  subject: string;
  correctPct: number;
  subjectUnitId?: number | null;
}

type Period = 'Bu Hafta' | 'Bu Ay' | 'Tüm Zamanlar';

const periodToParam: Record<Period, string> = {
  'Bu Hafta': 'week',
  'Bu Ay': 'month',
  'Tüm Zamanlar': 'all',
};

const TestCard: React.FC<{ test: TestResult; accentColor: string; onClick?: () => void; isActive?: boolean }> = ({ test, accentColor, onClick, isActive }) => {
  // Cards represent source-backed konular. Click drills the
  // graph + stats down to that single subject_unit_id; isActive shows a
  // subtle outline so the student knows the chart is filtered.
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={`rounded-[14px] block w-full text-left ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      style={{
        background: accentColor,
        padding: 2,
        boxShadow: isActive ? '0px 0px 0px 3px #2b2b2b, 0px 1px 4px rgba(0,0,0,0.12)' : '0px 1px 4px rgba(0,0,0,0.12)',
      }}
    >
      <div className="flex rounded-[12px] overflow-hidden">
        <div className="flex-1 p-2.5 bg-[#fafafa]">
          <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{test.topic}</p>
          <p style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: '#2b2b2b', marginTop: 2 }}>{test.subject}</p>
        </div>
        <div
          className="w-[72px] flex flex-col items-center justify-center flex-shrink-0"
          style={{ background: accentColor }}
        >
          <span style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 600, color: '#ffffff' }}>Doğru</span>
          <span style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#ffffff' }}>%{test.correctPct}</span>
        </div>
      </div>
    </Wrapper>
  );
};

interface SubjectOption { id: number | 'all'; name: string }

// G-2: breakdown required in the goal/performance area. "Partial" means the
// student started the assignment (localStorage draft has ≥1 answer) but
// hasn't submitted. Everything else with status='pending' is "not completed".
interface HomeworkBreakdown {
  total: number;
  fullCompleted: number;
  partialCompleted: number;
  notCompleted: number;
  fullCompletedPct: number;
}


// Donut that visualizes the Doğru / Yanlış split with the D. Ort (average
// accuracy) in the center — replaces the old right-column chart that just
// re-plotted the Başarı Puanı line.
const AccuracyDonut: React.FC<{ correct: number; wrong: number; avg: number }> = ({ correct, wrong, avg }) => {
  const total = correct + wrong;
  const size = 150;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const correctFrac = total > 0 ? correct / total : 0;
  const dash = correctFrac * c;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {/* Full ring = Yanlış (pink); overlaid arc = Doğru (blue). */}
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5599b" strokeWidth={stroke} />
          {total > 0 && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="#3973bf"
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeLinecap={correctFrac > 0 && correctFrac < 1 ? 'round' : 'butt'}
            />
          )}
        </g>
        <text x={size / 2} y={size / 2 - 1} textAnchor="middle" fontFamily="Inter" fontSize="28" fontWeight="700" fill="#2b2b2b">%{avg}</text>
        <text x={size / 2} y={size / 2 + 19} textAnchor="middle" fontFamily="Inter" fontSize="12" fontWeight="500" fill="#888">D. Ort</text>
      </svg>
      <div className="flex gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#3973bf' }} />
          <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#2b2b2b' }}>Doğru {correct}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#e5599b' }} />
          <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#2b2b2b' }}>Yanlış {wrong}</span>
        </div>
      </div>
    </div>
  );
};

const StudentPerformance: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // T5 — jump from a topic (Digikoç comment / weak-topic list) to where it's
  // studied. Owned → open the book at that section; locked → the library so the
  // student can unlock the book that covers it.
  const goToTopic = (ref?: LibraryRef | null) => {
    if (!ref) return;
    if (ref.owned) {
      navigate('/student/kitap-icerik', {
        state: { bookId: Number(ref.book_id), autoOpenSection: ref.section_label ?? undefined },
      });
    } else {
      navigate('/student/kutuphanem');
    }
  };
  const initialTab = (): PageTab => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t === 'gunluk' || t === 'raporlar') return t;
    return 'performans';
  };
  const [activeTab, setActiveTab] = useState<PageTab>(initialTab);
  const [period, setPeriod] = useState<Period>('Bu Hafta');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [subject, setSubject] = useState<SubjectOption>({ id: 'all', name: 'Tüm Dersler' });
  const [subjectOptions, setSubjectOptions] = useState<SubjectOption[]>([{ id: 'all', name: 'Tüm Dersler' }]);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  // Daily question counts (Soru Sayısı chart). Same {label,value,submitted_at}
  // shape as chartData but value is a raw count, not a 0-100 percentage.
  const [questionsPerDay, setQuestionsPerDay] = useState<ChartDataPoint[]>([]);
  const [chartDomain, setChartDomain] = useState<{ min?: number; max?: number }>({});
  const [weakTopics, setWeakTopics] = useState<WeakTopic[]>([]);
  const [bestTests, setBestTests] = useState<TestResult[]>([]);
  const [worstTests, setWorstTests] = useState<TestResult[]>([]);
  const [stats, setStats] = useState({ correct: 0, wrong: 0, avg: 0, testCount: 0 });
  // Mirrors the parent Performansım caption — how much of the work the konu
  // panels can actually describe.
  const [attribution, setAttribution] = useState({ answered: 0, detailed: 0, attributed: 0 });
  const [loading, setLoading] = useState(true);
  const [homeworkBreakdown, setHomeworkBreakdown] = useState<HomeworkBreakdown>({
    total: 0, fullCompleted: 0, partialCompleted: 0, notCompleted: 0, fullCompletedPct: 0,
  });
  // AI Yorumu (robot rehber). Static one-shot fetch, no user text input.
  const [aiInsight, setAiInsight] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  // T5 — the "Eksik konularını çalış" links sit under the Digikoç comment,
  // which is all-time. Keep them ALL-TIME too (a fixed one-off fetch), so they
  // don't vanish when the chart period is "Bu Hafta" and there's no weekly data.
  const [insightTopics, setInsightTopics] = useState<WeakTopic[]>([]);

  const loadData = useCallback((p: Period, subj: SubjectOption, unitId: number | null) => {
    setLoading(true);
    studentApi.performance(periodToParam[p], subj.id === 'all' ? undefined : subj.id, unitId ?? undefined)
      .then((res) => {
        const data = res.data;
        // Backend includes `submitted_at` on each chart point — forward it so
        // dots space by real time rather than array index.
        const chart: ChartDataPoint[] = (data.chartData || []).map((p: any) => ({
          label: p.label,
          value: typeof p.value === 'number' ? p.value : Number(p.value) || 0,
          submitted_at: p.submitted_at,
        }));
        setChartData(chart);
        const qpd: ChartDataPoint[] = (data.questionsPerDay || []).map((p: any) => ({
          label: p.label,
          value: typeof p.value === 'number' ? p.value : Number(p.value) || 0,
          submitted_at: p.submitted_at,
        }));
        setQuestionsPerDay(qpd);
        // Use backend-supplied domain so the chart axis spans the correct calendar window
        if (data.domainMin && data.domainMax) {
          setChartDomain({ min: data.domainMin, max: data.domainMax });
        } else {
          setChartDomain({});
        }

        // Subjects the student has attempted — merged with "Tüm Dersler".
        // Keep current selection in the list even if it falls out of the
        // backend payload (e.g. no attempts in this period).
        if (Array.isArray(data.availableSubjects)) {
          const merged: SubjectOption[] = [
            { id: 'all', name: 'Tüm Dersler' },
            ...data.availableSubjects.map((s: any) => ({ id: s.id, name: s.name })),
          ];
          setSubjectOptions(merged);
        }

        // weak topics from topicPerformance
        const topics: WeakTopic[] = (data.weakTopics || []).map((t: any) => ({
          topic: t.subject_units?.name || 'Konu',
          subject: t.subject_units?.subjects?.name || '',
          wrongPct: t.wrong_rate != null ? Math.round(Number(t.wrong_rate) * 100) : 0,
          libraryRef: t.library_ref ?? null,
        }));
        setWeakTopics(topics);

        // best3/worst3 are subject_unit konu aggregates. Each row carries
        // title (konu name), subject, accuracy_pct, subject_unit_id (null
        // for the "Genel" fallback bucket when book_answer_keys.subject_unit_id
        // wasn't backfilled).
        const mapResult = (r: any): TestResult => ({
          topic: r.title || r.detail || r.topic || 'Konu',
          detail: r.subject_name || r.subject || '',
          subject: r.subject_name || r.subject || '',
          correctPct: typeof r.accuracy_pct === 'number' ? r.accuracy_pct
                    : typeof r.value === 'number' ? Math.round(r.value)
                    : 0,
          subjectUnitId: r.subject_unit_id ?? null,
        });

        setBestTests((data.best3 || []).map(mapResult));
        setWorstTests((data.worst3 || []).map(mapResult));

        // Prefer backend-aggregated stats (from book_section_attempts). Fall
        // back to subject_progress rollup if the new field is missing.
        if (data.stats) {
          setStats({
            correct: data.stats.correct ?? 0,
            wrong: data.stats.wrong ?? 0,
            avg: data.stats.accuracy_pct ?? 0,
            testCount: data.stats.attempts_count ?? 0,
          });
        } else {
          const sp = data.subjectProgress || [];
          const totalCorrect = sp.reduce((s: number, p: any) => s + (p.correct_count ?? 0), 0);
          const totalWrong = sp.reduce((s: number, p: any) => s + (p.wrong_count ?? 0), 0);
          const total = totalCorrect + totalWrong;
          setStats({
            correct: totalCorrect,
            wrong: totalWrong,
            avg: total > 0 ? Math.round((totalCorrect / total) * 100) : 0,
            testCount: data.chartData?.length || 0,
          });
        }
        const a = data.attribution ?? {};
        setAttribution({
          answered: a.answered ?? 0,
          detailed: a.detailed ?? 0,
          attributed: a.attributed ?? 0,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(period, subject, null); }, [period, subject, loadData]);

  // T5 — all-time weak topics for the "Eksik konularını çalış" links (fixed,
  // independent of the period selector so they persist under the all-time
  // Digikoç comment even on the empty "Bu Hafta" view).
  useEffect(() => {
    studentApi.performance('all')
      .then((res) => {
        const topics: WeakTopic[] = (res.data?.weakTopics || []).map((t: any) => ({
          topic: t.subject_units?.name || 'Konu',
          subject: t.subject_units?.subjects?.name || '',
          wrongPct: t.wrong_rate != null ? Math.round(Number(t.wrong_rate) * 100) : 0,
          libraryRef: t.library_ref ?? null,
        }));
        setInsightTopics(topics);
      })
      .catch(() => setInsightTopics([]));
  }, []);

  // G-2: pull the homework list so we can show fully/partially/not-completed
  // counts + overall completion %. Partial detection mirrors the logic used on
  // the assignments list (HomeworkTestRunner stores a draft under
  // `homework-test:ass-{id}` in localStorage when a student answers anything).
  useEffect(() => {
    studentApi.assignments()
      .then((res) => {
        const rows: any[] = res.data || [];
        const total = rows.length;
        let fullCompleted = 0;
        let partialCompleted = 0;
        let notCompleted = 0;
        for (const r of rows) {
          const isCompleted = ['submitted', 'graded', 'returned'].includes(r.status);
          if (isCompleted) { fullCompleted++; continue; }
          let hasDraft = false;
          try {
            const raw = localStorage.getItem(`homework-test:ass-${String(r.id)}`);
            if (raw) {
              const saved = JSON.parse(raw);
              hasDraft = !!saved?.answers && Object.keys(saved.answers).length > 0;
            }
          } catch { /* ignore */ }
          if (hasDraft) partialCompleted++;
          else notCompleted++;
        }
        setHomeworkBreakdown({
          total,
          fullCompleted,
          partialCompleted,
          notCompleted,
          fullCompletedPct: total > 0 ? Math.round((fullCompleted / total) * 100) : 0,
        });
      })
      .catch(() => { /* leave zeros */ });
  }, []);

  // AI Yorumu (Digikoç): one streamed call covering weekly + monthly + all-time
  // performance, so no regeneration is needed when the period view changes.
  // Runs once on mount; renders token-by-token as the stream arrives.
  useEffect(() => {
    const controller = new AbortController();
    setAiLoading(true);
    setAiUnavailable(false);
    setAiInsight('');
    setAiStreaming(false);
    (async () => {
      try {
        const res = await studentApi.performanceInsightStream(controller.signal);
        if (!res.ok || !res.body) { setAiUnavailable(true); setAiLoading(false); return; }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let acc = '';
        let first = true;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (first) { first = false; setAiLoading(false); setAiStreaming(true); }
          acc += decoder.decode(value, { stream: true });
          setAiInsight(acc);
        }
        setAiStreaming(false);
        setAiLoading(false);
        if (!acc.trim()) setAiUnavailable(true);
      } catch (e: any) {
        if (e?.name !== 'AbortError') { setAiUnavailable(true); setAiLoading(false); setAiStreaming(false); }
      }
    })();
    return () => controller.abort();
  }, []);

  const displayChart = chartData.length > 0
    ? chartData
    : Array.from({ length: 7 }, (_, i) => ({ label: `Gün ${i + 1}`, value: 0 }));

  const displayQuestions = questionsPerDay.length > 0
    ? questionsPerDay
    : Array.from({ length: 7 }, (_, i) => ({ label: `Gün ${i + 1}`, value: 0 }));
  // Round the Y-axis top up to a tidy multiple of 10 (min 10) so the count
  // axis reads cleanly regardless of the busiest day.
  const soruYMax = Math.max(10, Math.ceil(Math.max(0, ...questionsPerDay.map((d) => d.value)) / 10) * 10);

  // chartDomain comes from the backend — it reflects Monday–Sunday or 1st–last day,
  // not a rolling window. This eliminates repeated X-axis labels.
  const periodDomain = chartDomain;

  const TAB_CONFIG: { key: PageTab; label: string; color: string }[] = [
    { key: 'performans', label: 'Performansım',    color: '#ffda09' },
    { key: 'gunluk',     label: 'Günlük Verilerim', color: '#f87aff' },
    { key: 'raporlar',   label: 'Raporlarım',       color: '#6d79ff' },
  ];

  return (
    <div>
      {/* Tab switcher */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {TAB_CONFIG.map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 xl:px-5 py-2 xl:py-2.5 rounded-[12px] text-[12px] xl:text-[16px] font-bold transition-colors ${activeTab === key ? 'text-white' : 'bg-[#fafafa] text-[#2b2b2b] border border-gray-200'}`}
            style={activeTab === key ? { backgroundColor: color } : undefined}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'gunluk' && <StudentDailyData />}
      {activeTab === 'raporlar' && <StudentReports />}

      {activeTab === 'performans' && <div>
      <div className="flex items-center justify-between mb-6">
        <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Performansım</h1>
        {/* Global chart filters — Dönem + Ders govern Başarı Puanı, Soru Sayısı
            ve Doğru/Yanlış Oranı together. Ödev Özeti is intentionally NOT
            filtered (it's an all-time homework rollup fetched separately). */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <div
              className="bg-[#fafafa] rounded-[12px] px-5 py-3 flex items-center gap-2 cursor-pointer"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
              onClick={() => setShowPeriodDropdown((v) => !v)}
            >
              <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>{period}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="#2b2b2b"><path d="M1 1l5 5 5-5" /></svg>
            </div>
            {showPeriodDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-[12px] shadow-lg border border-gray-200 z-20 overflow-hidden min-w-[150px]">
                {(['Bu Hafta', 'Bu Ay', 'Tüm Zamanlar'] as Period[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => { setPeriod(p); setShowPeriodDropdown(false); }}
                    className={`block w-full text-left px-5 py-3 text-[14px] font-semibold hover:bg-gray-50 ${period === p ? 'text-[#3973bf]' : 'text-[#2b2b2b]'}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <div
              className="bg-[#fafafa] rounded-[12px] px-5 py-3 flex items-center gap-2 cursor-pointer"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
              onClick={() => setShowSubjectDropdown((v) => !v)}
            >
              <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>{subject.name}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="#2b2b2b"><path d="M1 1l5 5 5-5" /></svg>
            </div>
            {showSubjectDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-[12px] shadow-lg border border-gray-200 z-20 overflow-hidden min-w-[180px]">
                {subjectOptions.map((opt) => (
                  <button
                    key={String(opt.id)}
                    onClick={() => { setSubject(opt); setShowSubjectDropdown(false); }}
                    className={`block w-full text-left px-5 py-3 text-[14px] font-semibold hover:bg-gray-50 ${subject.id === opt.id ? 'text-[#3973bf]' : 'text-[#2b2b2b]'}`}
                  >
                    {opt.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* G-2: homework + attendance breakdown required in the goal/performance
          area. Kept as a compact strip so the existing chart/stats layout
          below stays unchanged. */}
      <div className="bg-[#fafafa] rounded-[24px] p-5 mb-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>
            Ödev Özeti
          </h2>
          <span style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#3973bf' }}>
            Tamamlama %{homeworkBreakdown.fullCompletedPct}
          </span>
        </div>
        {/* Segmented completion bar: green = fully done, yellow = partial,
            red = not done. Widths are proportional to the homework counts so
            the whole bar always fills when there is at least one assignment. */}
        <div className="w-full h-[12px] bg-[#e5e5e5] rounded-[8px] mb-4 overflow-hidden flex">
          {homeworkBreakdown.total > 0 && (
            <>
              <div
                className="h-full"
                style={{ width: `${(homeworkBreakdown.fullCompleted / homeworkBreakdown.total) * 100}%`, background: HW_GREEN }}
                title={`Tamamen Tamamlandı: ${homeworkBreakdown.fullCompleted}`}
              />
              <div
                className="h-full"
                style={{ width: `${(homeworkBreakdown.partialCompleted / homeworkBreakdown.total) * 100}%`, background: HW_YELLOW }}
                title={`Kısmen Tamamlandı: ${homeworkBreakdown.partialCompleted}`}
              />
              <div
                className="h-full"
                style={{ width: `${(homeworkBreakdown.notCompleted / homeworkBreakdown.total) * 100}%`, background: HW_RED }}
                title={`Hiç Yapılmadı: ${homeworkBreakdown.notCompleted}`}
              />
            </>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-[12px] p-3 text-center border border-gray-100">
            <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#888' }}>Toplam Ödev</p>
            <p style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, color: '#2b2b2b' }}>{homeworkBreakdown.total}</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 text-center border-2" style={{ borderColor: HW_GREEN }}>
            <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#3aa055' }}>Tamamen Tamamlandı</p>
            <p style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, color: HW_GREEN }}>{homeworkBreakdown.fullCompleted}</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 text-center border-2 border-[#f5c518]">
            <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#a97e00' }}>Kısmen Tamamlandı</p>
            <p style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, color: '#f5c518' }}>{homeworkBreakdown.partialCompleted}</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 text-center border-2 border-[#e53e3e]">
            <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#c53030' }}>Hiç Yapılmadı</p>
            <p style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, color: '#e53e3e' }}>{homeworkBreakdown.notCompleted}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-5">
        {/* LEFT COLUMN */}
        <div className="flex-1 min-w-0">
          <div className="bg-[#fafafa] rounded-[24px] p-6 mb-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            {/* Başarı Puanı on top, Soru Sayısı (questions solved per day)
                below it. The Dönem filter that governs both (and the donut in
                the right column) now lives in the page header. */}
            <div className="flex flex-col gap-5">
              <div>
                <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#2b2b2b', marginBottom: 4, textAlign: 'center' }}>
                  Başarı Puanı (0 - 100)
                </p>
                <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: '#8a8a99', marginBottom: 8, textAlign: 'center' }}>
                  Çözdüğün testlerin doğruluk oranından hesaplanan 0–100 arası başarı puanın; zaman içindeki gidişatını gösterir.
                </p>
                <div className="bg-[#fafafa] rounded-[12px]" style={{ height: 320, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <PerformanceChart data={displayChart} color="#3973bf" id="main-perf" height={320} strokeWidth={6} domainMin={periodDomain.min} domainMax={periodDomain.max} />
                </div>
              </div>
              <div>
                <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#2b2b2b', marginBottom: 4, textAlign: 'center' }}>
                  Soru Sayısı
                </p>
                <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: '#8a8a99', marginBottom: 8, textAlign: 'center' }}>
                  Her gün çözdüğün soru adedi; çalışma düzenini ve yoğunluğunu gösterir.
                </p>
                <div className="bg-[#fafafa] rounded-[12px]" style={{ height: 320, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                  <PerformanceChart data={displayQuestions} color="#7c5cff" id="soru-count" height={320} strokeWidth={6} domainMin={periodDomain.min} domainMax={periodDomain.max} yMax={soruYMax} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-5">
            <div className="flex-1">
              <h2 style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 2 }}>En İyi 3 Konu</h2>
              <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: '#8a8a99', marginBottom: 8 }}>Doğruluk oranın en yüksek olan konular.</p>
              {loading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
              ) : bestTests.length === 0 ? (
                <p className="text-gray-400 text-sm">Veri yok.</p>
              ) : (
                <div className="space-y-3">
                  {bestTests.map((t, i) => (
                    <TestCard
                      key={i}
                      test={t}
                      accentColor="#3973bf"
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1">
              <h2 style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 2 }}>En Zayıf 3 Konu</h2>
              <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: '#8a8a99', marginBottom: 8 }}>Doğruluk oranın en düşük olan konular.</p>
              {loading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-[#e5599b] border-t-transparent rounded-full animate-spin" /></div>
              ) : worstTests.length === 0 ? (
                <p className="text-gray-400 text-sm">Veri yok.</p>
              ) : (
                <div className="space-y-3">
                  {worstTests.map((t, i) => (
                    <TestCard
                      key={i}
                      test={t}
                      accentColor="#e5599b"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div
          className="w-[260px] xl:w-[300px] flex-shrink-0 bg-[#fafafa] rounded-[16px] overflow-hidden self-start"
          style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
        >
          <div className="bg-[#e5599b] px-4 py-2 flex items-center justify-around" style={{ borderRadius: '16px 16px 0 0' }}>
            <div className="text-center">
              <p style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: '#ffffff' }}>Doğru</p>
              <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#fafafa' }}>{stats.correct}</p>
            </div>
            <div className="text-center">
              <p style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: '#ffffff' }}>Yanlış</p>
              <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#fafafa' }}>{stats.wrong}</p>
            </div>
            <div className="text-center">
              <p style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: '#ffffff' }}>D. Ort</p>
              <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#fafafa' }}>%{stats.avg}</p>
            </div>
          </div>

          <div className="p-4">
            <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 700, color: '#2b2b2b', marginBottom: 2 }}>Doğru / Yanlış Oranı</p>
            <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: '#8a8a99', marginBottom: 6 }}>Çözdüğün tüm testlerdeki doğru ve yanlış cevaplarının dağılımı.</p>
            <div className="rounded-[12px] mb-3 flex items-center justify-center py-4" style={{ border: '1px solid #e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <AccuracyDonut correct={stats.correct} wrong={stats.wrong} avg={stats.avg} />
            </div>
            <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 700, color: '#2b2b2b' }}>
              Test Adedi : {stats.testCount}
            </p>
            {/* Same caption as the parent Performansım panel — konu analysis
                covers only questions tagged to a subject_unit, and older submissions
                stored a score with no per-question detail. */}
            {attribution.answered > 0 && attribution.attributed < attribution.answered && (
              <p style={{ fontFamily: 'Inter', fontSize: 11, color: '#8a8a99', marginTop: 6, lineHeight: 1.45 }}>
                Konu bazlı analiz, çözdüğün {attribution.answered} sorunun{' '}
                {attribution.attributed} tanesini kapsıyor. Eski sürümlerle gönderilen
                testlerde soru bazlı kayıt tutulmadığı için bu testler konu
                dağılımında görünmez; yukarıdaki toplamlara dâhildir.
              </p>
            )}

            {/* AI Yorumu — robot rehber. Text comes from Gemini via the backend;
                static one-shot, no user input. */}
            <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0d0e0' }}>
              <div className="flex flex-col items-center mb-2">
                <img src="/a-digikoc-mascot.png" alt="Digikoç" style={{ width: 120, height: 120, objectFit: 'contain' }} />
                <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#2b2b2b', marginTop: 4 }}>Digikoç Yorumu</p>
              </div>
              <div
                className="rounded-[12px] px-3 py-3"
                style={{ background: '#f7edf3', border: '1px solid #f0d0e0' }}
              >
                {aiLoading ? (
                  <div className="flex items-center gap-2 py-1">
                    <div className="w-4 h-4 border-2 border-[#e5599b] border-t-transparent rounded-full animate-spin" />
                    <span style={{ fontFamily: 'Inter', fontSize: 12, color: '#888' }}>Rehberin yorumu hazırlanıyor…</span>
                  </div>
                ) : aiInsight ? (
                  <p style={{ fontFamily: 'Inter', fontSize: 12.5, lineHeight: 1.55, color: '#2b2b2b', whiteSpace: 'pre-line' }}>
                    {aiInsight}
                    {aiStreaming && <span className="inline-block w-[7px] h-[13px] ml-0.5 align-[-1px] bg-[#e5599b] animate-pulse" />}
                  </p>
                ) : (
                  <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#888' }}>
                    {aiUnavailable ? 'Yapay zeka yorumu şu an kullanılamıyor.' : 'Yorum bulunamadı.'}
                  </p>
                )}
              </div>

              {/* T5 — from the Digikoç comment, jump straight to the library
                  topic. Owned konular open the book+section; ones only in a
                  book she doesn't own show a lock and route to the library. */}
              {insightTopics.some((t) => t.libraryRef) && (
                <div className="mt-3">
                  <p style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 700, color: '#2b2b2b', marginBottom: 6 }}>
                    Eksik konularını çalış
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {insightTopics.filter((t) => t.libraryRef).map((t, i) => {
                      const ref = t.libraryRef!;
                      return (
                        <button
                          key={i}
                          onClick={() => goToTopic(ref)}
                          className="w-full text-left rounded-[10px] px-3 py-2 flex items-center justify-between gap-2 hover:opacity-90 transition-opacity"
                          style={{ background: ref.owned ? '#eef3ff' : '#f3f3f5', border: `1px solid ${ref.owned ? '#c9d8ff' : '#e0e0e5'}` }}
                          title={ref.owned ? `${ref.book_title} kitabında çalış` : `${ref.book_title} — kilitli`}
                        >
                          <span className="min-w-0">
                            <span style={{ fontFamily: 'Inter', fontSize: 12.5, fontWeight: 600, color: '#2b2b2b', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.topic}</span>
                            <span style={{ fontFamily: 'Inter', fontSize: 11, color: '#888', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ref.owned ? ref.book_title : `Kilitli · ${ref.book_title}`}
                            </span>
                          </span>
                          {ref.owned ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M9 6l6 6-6 6" /></svg>
                          ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" style={{ flexShrink: 0 }}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>}
    </div>
  );
};

export default StudentPerformance;
